import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReserveCardGuest from 'components/reserveCardGuest/index';
import axios from 'utils/axiosUtil';
import { GUEST_RESERVE_CANCEL } from 'constants/apiURL';
import GuestCancelModal from 'pages/guestReservation/modals/guestCancelModal';
import Proctitle from 'components/proctitle/index';
import Linked from 'components/linked/index';
import { useDeviceSize } from 'hooks/useDeviceSize';
import Icons from 'images/icons';
import AprosButton from 'components/aprosButton/index';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
} from '@material-tailwind/react';
import Path, { ERROR_MESSAGE_TRANSITION } from 'constants/routePath';
import { useDispatch } from 'react-redux';
import { messageModalActions } from 'slices/messageModalSlice';
import { getTermKbn } from 'utils/otherUtil';
import Title from '../../constants/title';
import { Helmet } from 'react-helmet';

const GuestInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isSp } = useDeviceSize();
  const [openCaution, setOpenCaution] = useState(false);
  const handleOpenCaution = () => setOpenCaution(cur => !cur);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const openCancelModal = () => setIsCancelModalOpen(true);
  const closeCancelModal = () => setIsCancelModalOpen(false);
  const [isCanceled, setIsCanceled] = useState(location.state?.isCanceled);

  useEffect(() => {
    if (!location.state?.guestReservationInfo) {
      const message = ERROR_MESSAGE_TRANSITION;
      dispatch(messageModalActions.showMessageModal({ message }));
      navigate(Path.GUEST_RESERVATION);
      return;
    }
  }, [location.state, navigate]);

  function Icon({ open }) {
    return (
      <svg
        xmlns={Icons.open01}
        className={`${open ? 'rotate-180' : ''} -ml-3 h-5 w-5 lg:-ml-10`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={3}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    );
  }

  const { email, reserveNumber, reservationNumber, guestReservationInfo } =
    location.state || {};

  const formatTime = timeStr => {
    if (!timeStr || timeStr.length !== 4) return timeStr;
    return `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}`;
  };

  const formatHms = hms => {
    if (!hms || hms.length !== 4) return '';
    return `${hms}00`;
  };

  const shopAndDateInfo = {
    shop: {
      shopName: guestReservationInfo?.tokNmRyk,
      shopCode: guestReservationInfo?.tokCd,
    },
    date: guestReservationInfo?.work?.[0]?.sgyYmd,
    time: formatTime(guestReservationInfo?.work?.[0]?.sgyStaHm),
    yykGetYmd: guestReservationInfo?.work?.[0]?.sgyYmd,
    yykGetHms: guestReservationInfo?.work?.[0]?.sgyStaHm,
    sgySeq: guestReservationInfo?.work?.[0]?.sgySeq,
  };

  const carInfo = {
    manufacturer: guestReservationInfo?.sryMkrMei,
    carModel: guestReservationInfo?.sshMei,
    year: guestReservationInfo?.sryNnskYm,
    mileage: guestReservationInfo?.sokKyr,
  };

  const userInfo = {
    email: guestReservationInfo?.mailAdr || email,
    name: guestReservationInfo?.simei,
    phoneNumber: guestReservationInfo?.telNo,
  };

  // 予約キャンセル処理
  const handleCancelGuestReservation = async () => {
    const termKbn = getTermKbn();
    const yykGetYmd = shopAndDateInfo?.yykGetYmd;
    const yykGetHms = formatHms(shopAndDateInfo?.yykGetHms);
    const shopCode = shopAndDateInfo?.shop?.shopCode;
    const yykNo = reservationNumber || reserveNumber;
    const sgySeq = guestReservationInfo?.work?.[0]?.sgySeq;

    // ゲスト予約情報キャンセルAPIを呼び出す
    const response = await axios.post(`${GUEST_RESERVE_CANCEL}`, {
      termKbn,
      yykGetYmd,
      yykGetHms,
      shopCode,
      yykNo,
      sgySeq,
    });
    dispatch(
      messageModalActions.showToastModal({
        message: '予約をキャンセルしました。',
      })
    );
    setTimeout(() => {
      setIsCanceled(true);
      closeCancelModal();
    });
  };

  // モーダルに渡すデータ
  const cancelModalData = {
    reserveId: guestReservationInfo?.yykNo,
    shopName: shopAndDateInfo?.shop?.shopName,
    vehicleName: [
      [carInfo?.manufacturer, carInfo?.carModel].filter(Boolean).join(' '),
      carInfo?.year ? `年式：${carInfo.year}年` : null,
      carInfo?.mileage ? `走行距離：${carInfo.mileage}km` : null,
    ].filter(Boolean),
    reserveDate:
      shopAndDateInfo?.date && shopAndDateInfo?.time
        ? `${shopAndDateInfo.date} ${shopAndDateInfo.time}`
        : shopAndDateInfo?.date,
  };

  // キャンセル状態を予約画面に渡す
  const handleGoToShopAndDateSelect = () => {
    navigate(Path.GUEST_SHOP_AND_DATE_SELECT, {
      state: {
        ...location.state,
        isCanceled: true,
        from: 'GUEST_SEARCH',
      },
    });
  };

  return (
    <div className="mt-4 lg:px-20">
      <Helmet>
        <title>{Title.GUEST_RESERVATION}</title>
      </Helmet>
      <div
        className={`mt-12 ${
          isSp ? 'sm:text-center sm:justify-center' : 'flex justify-center'
        }`}
      >
        <Proctitle
          useH1={isSp}
          text="予約確認"
          className={{
            textClass: 'whitespace-nowrap',
            areaClass: !isSp ? '!gap-0 !flex-none' : '',
          }}
        />
      </div>
      {isCanceled ? renderAfterCancel() : renderBeforeCancel()}
    </div>
  );

  // キャンセル前の表示内容
  function renderBeforeCancel() {
    return (
      <>
        <div className="mt-8 mb-4 justify-center">
          <p className="text-sm sm:justify-center sm:text-center sm:px-10 lg:px-10">
            日時を変更する場合は、一度予約をキャンセルし、再度ご予約をお願いします。
            <br />
            この画面で予約キャンセルができない場合は、ご予約の店舗へ直接ご連絡ください。
          </p>
        </div>
        <div className="flex justify-center sm:px-10 lg:px-10">
          <div className="w-full sm:min-w-[400px]">
            <Accordion open={openCaution} icon={<Icon open={openCaution} />}>
              <AccordionHeader
                onClick={handleOpenCaution}
                className="h-[48px] h-auto border-b-0 bg-navy/10 py-0 pl-[13px] font-bold lg:pl-[40px]"
              >
                <div className="flex items-center font-bold">
                  <div className="py-3 text-left text-[14px] sm:text-[18px]">
                    【注意事項】
                  </div>
                </div>
              </AccordionHeader>
              <AccordionBody className="text-sm pl-[15px] pr-1 lg:px-10 lg:pl-[30px]">
                <ul className="list-none pl-0">
                  <li className="flex items-start mb-2">
                    <span className="min-w-[1em]">1.</span>
                    <span className="break-words">
                      時間には余裕をもってご来店いただき、受付をお願い致します。<br />
                      (開店時刻と同時刻にご予約いただいた場合は、開店時刻にて受付をお願いします。)
                    </span>
                  </li>
                  <li className="flex items-start mb-2">
                    <span className="min-w-[1em]">2.</span>
                    <span className="break-words">
                      ご予約のお時間に、連絡なくご来店いただけない場合はキャンセル扱いとさせていただきます。
                    </span>
                  </li>
                  <li className="flex items-start mb-2">
                    <span className="min-w-[1em]">3.</span>
                    <span className="break-words">
                      WEBからのキャンセルの受付は、作業予約日の前日１２時までとさせていただきます。以降は店舗に直接電話連絡をお願いいたします。
                    </span>
                  </li>
                  <li className="flex items-start mb-2">
                    <span className="min-w-[1em]">4.</span>
                    <span className="break-words">
                      ご予約の内容について、店舗より電話にて確認をさせていただく場合がございます。
                    </span>
                  </li>
                  <li className="flex items-start mb-2">
                    <span className="min-w-[1em]">5.</span>
                    <span className="break-words">
                      ご指定のメールアドレスに確認メールを送信いたしますが、念のため予約番号をお控えください。
                    </span>
                  </li>
                  <li className="flex items-start mb-2">
                    <span className="min-w-[1em]">6.</span>
                    <span className="break-words">
                      携帯メールのドメイン指定受信等（迷惑メール設定）をされている方は、以下のメールアドレスからの受信ができる様に、設定をお願いいたします。<br />
                      送信元アドレス：rsv-info@autobacs.com
                      ※設定を行わない場合、予約受付通知・前日のご案内などのメールが届かない場合がございます。
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="min-w-[1em]">7.</span>
                    <span className="break-words">
                      不正改造車に対する作業はお断りする場合がございます。あらかじめご了承ください。
                    </span>
                  </li>
                </ul>
              </AccordionBody>
            </Accordion>
          </div>
        </div>
        <div className="mt-8 flex justify-center sm:px-10 lg:px-10">
          <ReserveCardGuest
            guestFlag={true}
            reserveData={{
              reservationNumber: guestReservationInfo?.yykNo,
              date: shopAndDateInfo?.date,
              time: shopAndDateInfo?.time,
              shopName: shopAndDateInfo?.shop?.shopName,
              carInfo,
              userInfo,
            }}
            cancelableFlag={guestReservationInfo?.cancelableFlag}
            alertFlag={guestReservationInfo?.alertFlag}
            linkArea={
              guestReservationInfo?.cancelableFlag === '1' && (
                <div className="justify-center">
                  <Linked
                    text="予約キャンセル"
                    onClick={openCancelModal}
                    isLine
                    linkIcon="R"
                    className="font-bold"
                  />
                </div>
              )
            }
          />
        </div>
        <div className="mt-6 mb-12 flex justify-center items-center text-center sm:mt-12 sm:mb-24">
          <Linked
            text="戻る"
            onClick={() => navigate(Path.GUEST_SEARCH)}
            isLine
            linkIcon="L"
            className="font-bold"
          />
        </div>
        <GuestCancelModal
          isModalOpen={isCancelModalOpen}
          closeModal={closeCancelModal}
          data={cancelModalData}
          onClickCancel={handleCancelGuestReservation}
        />
      </>
    );
  };

  // キャンセル後の表示内容
  function renderAfterCancel() {
    return (
      <>
        <div className="mt-12 sm:mt-24 sm:text-center text-[18px]">
          現在ご予約はありません。
        </div>
        <div className="mt-12 flex justify-center hover:text-navy sm:mt-24">
          <AprosButton
            text="車買取査定をWEBで予約"
            comClassName="sub"
            onClick={handleGoToShopAndDateSelect}
            className="mb-4 w-[260px]"
          />
        </div>
        <div className="mt-8 mb-12 flex justify-center sm:mb-24">
          <Linked
            text="オートバックスカーズの車買取について"
            onClick={() =>
              window.open(
                'https://www.autobacs.com/static_html/cars/kaitori/top.html'
              )
            }
            isLine
            linkIcon="R"
            className="font-bold"
          />
        </div>
      </>
    );
  }
};

export default GuestInfo;
